import { CSSProperties, ReactElement } from 'react'
import Select, { Props as ReactSelectProps, createFilter } from 'react-select'

import { TestIdProps } from 'designSystem/types/propTypes'

import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_SIZE } from 'designSystem/foundations/constants/fontSize'
import { SIZE } from 'designSystem/foundations/constants/size'

import PDSIcon from '../PDSIcon/PDSIcon'
import PDSText from '../PDSText/PDSText'
import PDSTooltip from '../PDSTooltip/PDSTooltip'
import INTERNALxDiv from '../internal/elements/INTERNALxDiv'
import INTERNALxLabel from '../internal/elements/INTERNALxLabel'

export type DropdownVariant = 'normal' | 'purple'

type BaseProps = ReactSelectProps & TestIdProps

type DropdownProps = BaseProps & {
  clearValueLabel?: string
  error?: string
  isRequired?: boolean
  label?: string
  menuShouldBlockScroll?: boolean
  onClear?: () => void
  styles?: CSSProperties
  tooltip?: ReactElement | string
  variant?: DropdownVariant
}

/**
 * A wrapper for React Select
 * Look into building the component in house if more flexibility is needed
 */
export default function PDSDropdown({
  clearValueLabel = 'Clear',
  components,
  error,
  isClearable = false,
  isDisabled,
  isRequired = false,
  label,
  menuShouldBlockScroll = true,
  onClear = () => null,
  options = [],
  placeholder,
  styles,
  testId,
  tooltip,
  value,
  variant = 'normal',
  ...props
}: DropdownProps) {
  const selectedValue = options.find((option: any) => option?.value === value)
  const showClear = isClearable && !(value === null || value === undefined)
  const disabledClass = isDisabled ? 'disabled' : ''

  return (
    <INTERNALxLabel
      className={`dropdown-container ${disabledClass} ${variant}`}
      testId={testId}
      width="100%"
    >
      {(label || showClear) && (
        <INTERNALxDiv
          alignItems="center"
          display="flex"
          height="2rem"
          justifyContent="space-between"
        >
          <INTERNALxDiv alignItems="center" display="flex" gap={SIZE.s8}>
            <PDSText color={COLOR.textSecondary} size="md" weight="medium">
              {label}{' '}
              {isRequired && <PDSText color={COLOR.textSecondary}>*</PDSText>}
            </PDSText>
            {tooltip && <PDSTooltip content={tooltip} />}
          </INTERNALxDiv>

          {showClear && (
            <PDSText
              className="clearable"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault()
                onClear()
              }}
              size="sm"
              testId={testId && `${testId}-clear`}
              textDecoration="underline"
              weight="semibold"
            >
              {clearValueLabel}
            </PDSText>
          )}
        </INTERNALxDiv>
      )}

      <Select
        className={`${error ? 'error' : ''} ${
          testId ? `testid-${testId}` : ''
        }`}
        classNamePrefix="dropdown"
        components={{ IndicatorSeparator: null, ...components }}
        isDisabled={isDisabled}
        filterOption={createFilter({ ignoreAccents: true })}
        menuPlacement="auto"
        menuPosition="fixed"
        menuShouldBlockScroll={menuShouldBlockScroll}
        options={options}
        placeholder={placeholder}
        styles={{
          control: (base: any) => ({
            ...base,
            fontSize: FONT_SIZE.fs14,
            height: '2.75rem',
          }),
          menu: (base: any) => ({
            ...base,
            fontSize: FONT_SIZE.fs14,
            textAlign: 'left',
          }),
          ...styles,
        }}
        value={(selectedValue || value) ?? null}
        {...props}
      />
      {error && (
        <INTERNALxDiv
          alignItems="center"
          display="flex"
          gap={SIZE.s4}
          marginTop={SIZE.s4}
        >
          <PDSIcon color={COLOR.alert400} name="exclamationCircle" />
          <PDSText color={COLOR.alert400} size="md" weight="medium">
            {error}
          </PDSText>
        </INTERNALxDiv>
      )}
    </INTERNALxLabel>
  )
}
